// Runtime configuration reader (Phase 4 on-prem).
//
// On-prem report-web deployments mount a `/config.js` that assigns
// `window.__METASENS_CONFIG__` before the app bundle loads, letting a single
// static build be re-pointed per install without rebuilding. In the cloud,
// `public/config.js` ships only the report-revamp defaults (`reportRenderer`,
// `reportCompare` off, `reportContact` ''), so every other getter here falls through
// to the existing build-time env / hard-coded default.
//
// Consume config ONLY through these helpers; do not read
// window.__METASENS_CONFIG__ directly elsewhere.

function config() {
  return (typeof window !== 'undefined' && window.__METASENS_CONFIG__) || {}
}

// A string override only wins when it is a non-empty string; otherwise the
// caller's build-time fallback is used (so an empty '' in config = fallback).
function str(key, fallback) {
  const v = config()[key]
  return typeof v === 'string' && v.length > 0 ? v : fallback
}

// API base URL for the single axios instance. Fallback: VUE_APP_API_URI / default.
export function getApiBaseUrl(fallback) {
  return str('apiBaseUrl', fallback)
}

// Base URL used to compose shareable report links / QR codes.
// Fallback: the existing $isDev() ternary passed by the caller.
export function getSiteBaseUrl(fallback) {
  return str('siteBaseUrl', fallback)
}

// HMAC-SHA256 secret for X-Request-Signature. Fallback: VUE_APP_API_SIGNATURE_SECRET.
export function getSignatureSecret(fallback) {
  return str('signatureSecret', fallback)
}

// Base64 AES-GCM key for decrypting report payloads (per install).
// Fallback: VUE_APP_REPORT_DECRYPTION_KEY. Only used when encryption is enabled.
export function getDecryptionKey(fallback) {
  return str('decryptionKey', fallback)
}

// Whether report payloads are transport-encrypted (AES-GCM response decryption).
// A runtime override only wins when it is an explicit boolean; otherwise the
// caller's build-time fallback (VUE_APP_ENABLE_REPORT_ENCRYPTION === 'true') is used.
export function isEncryptionEnabled(fallback) {
  const v = config().encryptionEnabled
  return typeof v === 'boolean' ? v : fallback
}

// Whether report API requests are HMAC-signed (X-Request-Signature headers).
// Split from encryptionEnabled to mirror the backend's independent switches
// (CZN_ENABLE_SIGNATURE_VALIDATION vs CZN_ENABLE_RESPONSE_ENCRYPTION): an
// on-prem install can require signed requests while serving plaintext
// responses (plain-http deployments have no crypto.subtle for decryption).
// When `signatureEnabled` is NOT an explicit boolean it FOLLOWS
// encryptionEnabled (runtime value, else the same build-time fallback), so
// cloud builds and older config.js files behave exactly as before.
export function isSignatureEnabled(fallback) {
  const v = config().signatureEnabled
  return typeof v === 'boolean' ? v : isEncryptionEnabled(fallback)
}

// Deployment profile of this install. The on-prem report-web config.js sets
// `deployProfile: 'onprem'` (mirrors the backend's DEPLOY_PROFILE=onprem
// switch group); the cloud build ships no such key, so this is false there.
// Gates UI that only exists on-prem — e.g. the quota voucher import block on
// /admin/contest: the backend mounts POST /v2/quota/voucher only under
// IsOnprem(), so showing it in the cloud would just 404.
export function isOnprem() {
  return config().deployProfile === 'onprem'
}

// Report renderer switch (report revamp ticket 14). Shape:
//   { default: 'legacy', overrides: { '<ReportType>:<V1|V2>': 'next', '<ReportType>:*': 'next' },
//     dev: { overrides: {…} } }
// Returned as-is (null when absent or not an object); validation and precedence
// live in the pure resolver src/report/renderer-switch.js (resolveRenderer),
// where every malformed level simply falls through — a missing key = all legacy
// (the on-prem config.js generated by install.sh does not carry it).
export function getReportRendererConfig() {
  const v = config().reportRenderer
  return v && typeof v === 'object' && !Array.isArray(v) ? v : null
}

// Inline comparison row switch (report revamp ticket 25; user decision U1). Shape:
//   { enabled: boolean, dev: { enabled: boolean } }
// Returned as-is (null when absent or not an object); validation and precedence
// live in the pure resolver src/report/compare-switch.js (resolveCompare), where
// a non-boolean level simply falls through — a missing key = off (the on-prem
// config.js generated by install.sh does not carry it).
export function getReportCompareConfig() {
  const v = config().reportCompare
  return v && typeof v === 'object' && !Array.isArray(v) ? v : null
}

// Contact line shown on report error screens (ticket 17). Trimmed; '' (or
// absent / not a string / whitespace only) = not configured → the UI shows its
// i18n default ("please contact your distributor").
export function getReportContact() {
  const v = config().reportContact
  return typeof v === 'string' ? v.trim() : ''
}
